'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Navbar from '@/components/ui/Navbar';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Car {
  id: string;
  name: string;
  year: string;
  km: string;
  fuel: string;
  transmission: string;
  price: string;
  image: string;
  alt: string;
  finnUrl: string;
}

interface FormData {
  name: string;
  phone: string;
  email: string;
  message: string;
}

const formTabs = ['Kontakt', 'Innbytte', 'Selg bil', 'Kommisjon'];

const formTitles: Record<string, string> = {
  Kontakt: 'Kontakt oss',
  Innbytte: 'Innbytte av bil',
  'Selg bil': 'Selg bilen din',
  Kommisjon: 'Kommisjon'
};

// ─── SVG Icons ───────────────────────────────────────────────────────────────
const PhoneIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.4 2 2 0 0 1 3 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>;


const CarIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
    <circle cx="7" cy="17" r="2" />
    <path d="M9 17h6" />
    <circle cx="17" cy="17" r="2" />
  </svg>;


const MessageCircleIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
  </svg>;


const ArrowRightIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>;


const ArrowLeftRightIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M8 3 4 7l4 4" />
    <path d="M4 7h16" />
    <path d="m16 21 4-4-4-4" />
    <path d="M20 17H4" />
  </svg>;


const CarFrontIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8" />
    <path d="M7 14h.01" />
    <path d="M17 14h.01" />
    <rect width="18" height="8" x="3" y="10" rx="2" />
    <path d="M5 18v2" />
    <path d="M19 18v2" />
  </svg>;


const HandCoinsIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
    <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
    <path d="m2 16 6 6" />
    <circle cx="16" cy="9" r="2.9" />
    <circle cx="6" cy="5" r="3" />
  </svg>;


const MapPinIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>;


const MailIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>;


const ClockIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>;

const FilterIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>;

const XIcon = ({ className }: {className?: string;}) =>
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>;


// ─── Logo ─────────────────────────────────────────────────────────────────────
const BilvalLogo = ({ className, white }: { className?: string; white?: boolean }) => {
  return (
    <span className={`flex items-center ${className ?? ''}`}>
      <img
        src="/assets/images/image-1781963366139.png"
        alt="Bilvalg logo"
        className="h-10 w-auto"
        style={white ? { filter: 'brightness(0) invert(1)' } : undefined}
      />
    </span>
  );
};



// ─── Intersection Observer Hook ───────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="relative pt-16 md:pt-20 min-h-[85vh] md:min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://bilvalg-find-your-ride.lovable.app/assets/hero-car-BLr2521K.jpg"
          alt="Bilvalg AS – bruktbilforhandler i Lørenskog"
          className="w-full h-full object-cover object-center"
          loading="eager" />
        {/* Overlay */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{ backgroundColor: 'hsl(218 65% 22% / 0.5)' }} />
      </div>

      {/* Content */}
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl space-y-5 sm:space-y-6">
          <p
            className="font-semibold text-xs sm:text-sm tracking-widest uppercase opacity-0 animate-fade-up"
            style={{ color: 'rgba(255,255,255,0.8)', animationDelay: '200ms' }}>
            Autorisert bruktbilforhandler i Lørenskog
          </p>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.08] tracking-tight opacity-0 animate-fade-up"
            style={{ color: '#fff', animationDelay: '350ms' }}>
            Finn din neste bil hos Bilvalg
          </h1>
          <p
            className="text-base sm:text-lg max-w-lg opacity-0 animate-fade-up"
            style={{ color: 'rgba(255,255,255,0.8)', animationDelay: '500ms' }}>
            Bruktbiler til gode priser. Vi tilbyr innbytte og kommisjon.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 opacity-0 animate-fade-up"
            style={{ animationDelay: '650ms' }}>
            <a
              href="#biler"
              className="inline-flex items-center justify-center px-6 sm:px-7 py-3.5 rounded-lg font-semibold text-sm hover:opacity-90 active:scale-[0.97] transition-all shadow-lg"
              style={{ backgroundColor: '#fff', color: 'hsl(218, 65%, 22%)' }}>
              Se våre biler
            </a>
            <a
              href="#kontakt"
              className="inline-flex items-center justify-center border-2 px-6 sm:px-7 py-3.5 rounded-lg font-semibold text-sm hover:bg-white/10 active:scale-[0.97] transition-all"
              style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>
              Kontakt oss
            </a>
          </div>
        </div>
      </div>
    </section>);

}

// ─── Car Card ─────────────────────────────────────────────────────────────────
function CarCard({ car }: {car: Car;}) {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border group hover:shadow-md transition-shadow" style={{ borderColor: 'hsl(218, 20%, 88%)' }}>
      <div className="aspect-[3/2] overflow-hidden bg-gray-100">
        {car.image ? (
          <img
            src={car.image}
            alt={car.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = '/assets/images/no_image.png';
            }}
          />
        ) : (
          <img
            src="/assets/images/no_image.png"
            alt="Bilde ikke tilgjengelig"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="p-4 space-y-3">
        <h3 className="font-display font-semibold text-sm leading-tight" style={{ color: 'hsl(0, 0%, 12%)' }}>
          {car.name}
        </h3>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs" style={{ color: 'hsl(218, 12%, 42%)' }}>
          {car.year && <span>{car.year}</span>}
          {car.km && <span>{car.km}</span>}
          {car.fuel && <span>{car.fuel}</span>}
          {car.transmission && <span>{car.transmission}</span>}
        </div>
        <p className="font-display font-bold text-lg" style={{ color: 'hsl(0, 0%, 12%)' }}>
          {car.price}
        </p>
        <div className="flex gap-2 pt-1">
          <Link
            href={`/biler/${car.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold hover:opacity-90 active:scale-[0.97] transition-all"
            style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}>
            <MessageCircleIcon className="w-3.5 h-3.5" />
            Se annonse
          </Link>
          <a
            href="#kontakt"
            className="flex items-center justify-center gap-1 border py-2 px-3 rounded-lg text-xs font-semibold hover:bg-primary/5 active:scale-[0.97] transition-all"
            style={{ borderColor: 'hsl(218, 65%, 22%)', color: 'hsl(218, 65%, 22%)' }}
            title="Innbytte">
            <ArrowRightIcon className="w-3.5 h-3.5" />
            Innbytte
          </a>
        </div>
      </div>
    </div>);

}

// ─── Cars Section ─────────────────────────────────────────────────────────────
interface CarFilters {
  make: string;
  model: string;
  priceMin: string;
  priceMax: string;
  yearMin: string;
  yearMax: string;
  mileageMax: string;
}

const defaultFilters: CarFilters = {
  make: '',
  model: '',
  priceMin: '',
  priceMax: '',
  yearMin: '',
  yearMax: '',
  mileageMax: '',
};

function parsePriceNum(price: string): number {
  const n = parseInt(price.replace(/\D/g, ''), 10);
  return isNaN(n) ? 0 : n;
}

function parseKmNum(km: string): number {
  const n = parseInt(km.replace(/\D/g, ''), 10);
  return isNaN(n) ? 0 : n;
}

function extractMake(name: string): string {
  // First word of the car name is typically the make
  return name.split(' ')[0] ?? '';
}

function CarsSection() {
  const { ref: headerRef, inView: headerInView } = useInView();
  const { ref: btnRef, inView: btnInView } = useInView();

  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CarFilters>(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>('default');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/cars')
      .then((res) => {
        if (!res.ok) return res.json().then((d) => Promise.reject(d.error || `Feil ${res.status}`));
        return res.json();
      })
      .then((data) => {
        console.log('[CarsSection] API response:', data);
        if (!cancelled) {
          setCars(data.cars ?? []);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[CarsSection] API error:', err);
        if (!cancelled) {
          setError(typeof err === 'string' ? err : 'Kunne ikke hente biler. Prøv igjen senere.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Derive unique makes from loaded cars
  const makes = Array.from(new Set(cars.map((c) => extractMake(c.name)).filter(Boolean))).sort();

  // Active filter count
  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  // Apply filters
  const filteredCars = cars.filter((car) => {
    if (filters.make && extractMake(car.name).toLowerCase() !== filters.make.toLowerCase()) return false;
    if (filters.model && !car.name.toLowerCase().includes(filters.model.toLowerCase())) return false;
    const price = parsePriceNum(car.price);
    if (filters.priceMin && price < parseInt(filters.priceMin, 10)) return false;
    if (filters.priceMax && price > parseInt(filters.priceMax, 10)) return false;
    const year = parseInt(car.year, 10);
    if (filters.yearMin && !isNaN(year) && year < parseInt(filters.yearMin, 10)) return false;
    if (filters.yearMax && !isNaN(year) && year > parseInt(filters.yearMax, 10)) return false;
    const km = parseKmNum(car.km);
    if (filters.mileageMax && km > parseInt(filters.mileageMax, 10)) return false;
    return true;
  });

  const sortedCars = [...filteredCars].sort((a, b) => {
    if (sortOrder === 'price-asc') return parsePriceNum(a.price) - parsePriceNum(b.price);
    if (sortOrder === 'price-desc') return parsePriceNum(b.price) - parsePriceNum(a.price);
    if (sortOrder === 'newest') return parseInt(b.year, 10) - parseInt(a.year, 10);
    if (sortOrder === 'mileage-asc') return parseKmNum(a.km) - parseKmNum(b.km);
    return 0;
  });

  const hasActiveFilters = activeFilterCount > 0;

  const resetFilters = () => setFilters(defaultFilters);

  return (
    <section id="biler" className="py-16 md:py-24 lg:py-28" style={{ backgroundColor: 'hsl(0, 0%, 98%)' }}>
      <div className="container px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-xl mx-auto mb-10 sm:mb-12 transition-all duration-700 ${
          headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 sm:mb-6"
            style={{ backgroundColor: 'hsl(218 65% 22% / 0.1)' }}>
            <CarIcon className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: 'hsl(218, 65%, 22%)' } as React.CSSProperties} />
          </div>
          <p className="font-semibold text-sm tracking-widest uppercase mb-3" style={{ color: 'hsl(218, 65%, 22%)' }}>
            Utvalg
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold tracking-tight" style={{ color: 'hsl(0, 0%, 12%)' }}>
            Biler til salgs
          </h2>
          <p className="mt-3 max-w-md mx-auto text-sm sm:text-base" style={{ color: 'hsl(218, 12%, 42%)' }}>
            Se et utvalg av bilene våre. Ta kontakt for mer informasjon eller for å avtale en prøvekjøring.
          </p>
        </div>

        {/* Filter Bar */}
        {!loading && !error && cars.length > 0 && (
          <div className="mb-6 sm:mb-8">
            {/* Toggle button */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setShowFilters((v) => !v)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border transition-all hover:shadow-sm"
                style={{
                  backgroundColor: showFilters ? 'hsl(218, 65%, 22%)' : '#fff',
                  borderColor: showFilters ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                  color: showFilters ? '#fff' : 'hsl(218, 65%, 22%)',
                }}>
                <FilterIcon className="w-4 h-4" />
                Filtrer biler
                {activeFilterCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: showFilters ? 'rgba(255,255,255,0.25)' : 'hsl(218, 65%, 22%)',
                      color: '#fff',
                    }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors hover:bg-red-50"
                    style={{ color: 'hsl(0, 65%, 50%)' }}>
                    <XIcon className="w-3.5 h-3.5" />
                    Nullstill filter
                  </button>
                )}
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 appearance-none"
                  style={{
                    borderColor: sortOrder !== 'default' ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                    color: 'hsl(0, 0%, 12%)',
                    backgroundColor: '#fff',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                    paddingRight: '32px',
                  }}>
                  <option value="default">Sorter etter</option>
                  <option value="price-asc">Pris: lav → høy</option>
                  <option value="price-desc">Pris: høy → lav</option>
                  <option value="newest">Nyeste først</option>
                  <option value="mileage-asc">Lavest km-stand</option>
                </select>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div
                className="rounded-xl border p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                style={{ backgroundColor: '#fff', borderColor: 'hsl(218, 20%, 88%)' }}>

                {/* Make */}
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(218, 12%, 42%)' }}>
                    Merke
                  </label>
                  <select
                    value={filters.make}
                    onChange={(e) => setFilters({ ...filters, make: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 appearance-none bg-no-repeat"
                    style={{
                      borderColor: filters.make ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                      color: 'hsl(0, 0%, 12%)',
                      backgroundColor: '#fff',
                    }}>
                    <option value="">Alle merker</option>
                    {makes.map((make) => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>

                {/* Model */}
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(218, 12%, 42%)' }}>
                    Modell
                  </label>
                  <input
                    type="text"
                    value={filters.model}
                    onChange={(e) => setFilters({ ...filters, model: e.target.value })}
                    placeholder="Søk modell..."
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{
                      borderColor: filters.model ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                      color: 'hsl(0, 0%, 12%)',
                      backgroundColor: '#fff',
                    }}
                  />
                </div>

                {/* Price range */}
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(218, 12%, 42%)' }}>
                    Pris (kr)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={filters.priceMin}
                      onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })}
                      placeholder="Min"
                      min={0}
                      className="w-1/2 border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                      style={{
                        borderColor: filters.priceMin ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                        color: 'hsl(0, 0%, 12%)',
                        backgroundColor: '#fff',
                      }}
                    />
                    <input
                      type="number"
                      value={filters.priceMax}
                      onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })}
                      placeholder="Maks"
                      min={0}
                      className="w-1/2 border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                      style={{
                        borderColor: filters.priceMax ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                        color: 'hsl(0, 0%, 12%)',
                        backgroundColor: '#fff',
                      }}
                    />
                  </div>
                </div>

                {/* Year range */}
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(218, 12%, 42%)' }}>
                    Årsmodell
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={filters.yearMin}
                      onChange={(e) => setFilters({ ...filters, yearMin: e.target.value })}
                      placeholder="Fra"
                      min={1990}
                      max={2030}
                      className="w-1/2 border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                      style={{
                        borderColor: filters.yearMin ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                        color: 'hsl(0, 0%, 12%)',
                        backgroundColor: '#fff',
                      }}
                    />
                    <input
                      type="number"
                      value={filters.yearMax}
                      onChange={(e) => setFilters({ ...filters, yearMax: e.target.value })}
                      placeholder="Til"
                      min={1990}
                      max={2030}
                      className="w-1/2 border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                      style={{
                        borderColor: filters.yearMax ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                        color: 'hsl(0, 0%, 12%)',
                        backgroundColor: '#fff',
                      }}
                    />
                  </div>
                </div>

                {/* Mileage max */}
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'hsl(218, 12%, 42%)' }}>
                    Maks km-stand
                  </label>
                  <input
                    type="number"
                    value={filters.mileageMax}
                    onChange={(e) => setFilters({ ...filters, mileageMax: e.target.value })}
                    placeholder="f.eks. 100000"
                    min={0}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{
                      borderColor: filters.mileageMax ? 'hsl(218, 65%, 22%)' : 'hsl(218, 20%, 88%)',
                      color: 'hsl(0, 0%, 12%)',
                      backgroundColor: '#fff',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Results count */}
            {hasActiveFilters && (
              <p className="mt-3 text-sm" style={{ color: 'hsl(218, 12%, 42%)' }}>
                Viser <span className="font-semibold" style={{ color: 'hsl(0, 0%, 12%)' }}>{filteredCars.length}</span> av {cars.length} biler
              </p>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border animate-pulse" style={{ borderColor: 'hsl(218, 20%, 88%)' }}>
                <div className="aspect-[3/2] bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-5 bg-gray-200 rounded w-1/3" />
                  <div className="h-8 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="text-center py-16 max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'hsl(0, 80%, 95%)' }}>
              <CarIcon className="w-8 h-8" style={{ color: 'hsl(0, 65%, 50%)' } as React.CSSProperties} />
            </div>
            <p className="font-semibold mb-2" style={{ color: 'hsl(0, 0%, 12%)' }}>Kunne ikke laste biler</p>
            <p className="text-sm mb-6" style={{ color: 'hsl(218, 12%, 42%)' }}>{error}</p>
            <button
              onClick={() => { setLoading(true); setError(null); fetch('/api/cars').then(r => r.json()).then(d => { setCars(d.cars ?? []); setLoading(false); }).catch(e => { setError(typeof e === 'string' ? e : 'Prøv igjen senere.'); setLoading(false); }); }}
              className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-all"
              style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}>
              Prøv igjen
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && cars.length === 0 && (
          <div className="text-center py-16 max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'hsl(218 65% 22% / 0.1)' }}>
              <CarIcon className="w-8 h-8" style={{ color: 'hsl(218, 65%, 22%)' } as React.CSSProperties} />
            </div>
            <p className="font-semibold mb-2" style={{ color: 'hsl(0, 0%, 12%)' }}>Ingen biler tilgjengelig</p>
            <p className="text-sm" style={{ color: 'hsl(218, 12%, 42%)' }}>Vi har ingen biler til salgs akkurat nå. Sjekk tilbake snart eller kontakt oss.</p>
          </div>
        )}

        {/* No filter results */}
        {!loading && !error && cars.length > 0 && filteredCars.length === 0 && (
          <div className="text-center py-16 max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'hsl(218 65% 22% / 0.1)' }}>
              <FilterIcon className="w-8 h-8" style={{ color: 'hsl(218, 65%, 22%)' } as React.CSSProperties} />
            </div>
            <p className="font-semibold mb-2" style={{ color: 'hsl(0, 0%, 12%)' }}>Ingen biler matcher filteret</p>
            <p className="text-sm mb-5" style={{ color: 'hsl(218, 12%, 42%)' }}>Prøv å justere filtrene dine for å se flere resultater.</p>
            <button
              onClick={resetFilters}
              className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-all"
              style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}>
              Nullstill filter
            </button>
          </div>
        )}

        {/* Grid */}
        {!loading && !error && sortedCars.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {sortedCars.map((car) =>
              <CarCard key={car.id} car={car} />
            )}
          </div>
        )}

        {/* CTA Button */}
        <div
          ref={btnRef}
          className={`text-center mt-10 transition-all duration-700 delay-400 ${
          btnInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <a
            href="#biler"
            className="inline-flex items-center px-7 py-3 rounded-lg font-semibold text-sm hover:opacity-90 active:scale-[0.97] transition-all shadow-lg"
            style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}>
            Se alle biler
          </a>
        </div>
      </div>
    </section>);

}

// ─── Services Section ─────────────────────────────────────────────────────────
function ServicesSection() {
  const { ref: headerRef, inView: headerInView } = useInView();

  const services = [
  {
    icon: <ArrowLeftRightIcon className="w-6 h-6 text-white" />,
    title: 'Innbytte',
    description: 'Vi tar din gamle bil i innbytte — raskt og enkelt.',
    delay: '200ms'
  },
  {
    icon: <CarFrontIcon className="w-6 h-6 text-white" />,
    title: 'Selg bilen din',
    description: 'Vi kjøper bilen din. Få et uforpliktende tilbud.',
    delay: '300ms'
  },
  {
    icon: <HandCoinsIcon className="w-6 h-6 text-white" />,
    title: 'Kommisjon',
    description: 'Vi selger bilen din i kommisjon/formidling.',
    delay: '400ms'
  }];

  return (
    <section id="tjenester" className="py-16 md:py-24 lg:py-28" style={{ backgroundColor: 'hsl(218, 30%, 95%)' }}>
      <div className="container px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-xl mx-auto mb-10 sm:mb-14 transition-all duration-700 ${
          headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="font-semibold text-sm tracking-widest uppercase mb-3" style={{ color: 'hsl(218, 65%, 22%)' }}>
            Tjenester
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold tracking-tight" style={{ color: 'hsl(0, 0%, 12%)' }}>
            Hva vi tilbyr
          </h2>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {services.map((service) =>
          <ServiceCard key={service.title} service={service} />
          )}
        </div>
      </div>
    </section>);

}

function ServiceCard({ service }: {service: {icon: React.ReactNode;title: string;description: string;delay: string;};}) {
  const { ref, inView } = useInView();

  return (
    <a
      ref={ref as React.Ref<HTMLAnchorElement>}
      href="#kontakt"
      className={`rounded-xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group ${
      inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{
        backgroundColor: 'hsl(218, 65%, 22%)',
        transitionDelay: service.delay
      }}>
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors"
        style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
        {service.icon}
      </div>
      <h3 className="font-display font-semibold mb-2" style={{ color: '#fff' }}>
        {service.title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
        {service.description}
      </p>
    </a>);

}

// ─── Contact Section ──────────────────────────────────────────────────────────
function ContactSection() {
  const { ref: headerRef, inView: headerInView } = useInView();
  const { ref: contentRef, inView: contentInView } = useInView();

  const [activeTab, setActiveTab] = useState('Kontakt');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const formTypeLabel = activeTab || 'Kontakt';
      const subject = `Ny henvendelse: ${formTypeLabel} – Bilvalg AS`;
      const messageBody = [
        `Skjematype: ${formTypeLabel}`,
        `Navn: ${formData.name || '—'}`,
        `Telefon: ${formData.phone || '—'}`,
        `E-post: ${formData.email || '—'}`,
        `Melding: ${formData.message || '—'}`,
      ].join('\n');

      const payload: Record<string, string> = {
        access_key: '623fd222-644a-44dc-909e-034dcb3d7ca7',
        subject,
        from_name: formData.name || 'Bilvalg Nettside',
        message: messageBody,
      };

      if (formData.email) {
        payload.email = formData.email;
        payload.replyto = formData.email;
      }

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        setSubmitError(data.message || 'Noe gikk galt. Prøv igjen.');
      } else {
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '' });
        setTimeout(() => setSubmitted(false), 4000);
      }
    } catch {
      setSubmitError('Noe gikk galt. Prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-16 md:py-24 lg:py-28" style={{ backgroundColor: 'hsl(0, 0%, 98%)' }}>
      <div className="container px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-xl mx-auto mb-10 sm:mb-14 transition-all duration-700 ${
          headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="font-semibold text-sm tracking-widest uppercase mb-3" style={{ color: 'hsl(218, 55%, 32%)' }}>
            Kontakt
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold tracking-tight" style={{ color: 'hsl(0, 0%, 12%)' }}>
            Ta kontakt med oss
          </h2>
        </div>

        {/* Content Grid */}
        <div
          ref={contentRef}
          className={`grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto transition-all duration-700 delay-200 ${
          contentInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          
          {/* Contact Info */}
          <div className="rounded-xl p-6 sm:p-8 space-y-6" style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: '#fff' }}>
            <div className="flex items-center gap-3">
              <BilvalLogo white />
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <MapPinIcon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#fff' } as React.CSSProperties} />
                <span>
                  Fjellhamarveien 52<br />
                  Lørenskog, Norge
                </span>
              </div>
              <div className="flex items-center gap-3">
                <PhoneIcon className="w-5 h-5 shrink-0" style={{ color: '#fff' } as React.CSSProperties} />
                <a href="tel:+4741159508" className="hover:underline">411 59 508</a>
              </div>
              <div className="flex items-center gap-3">
                <MailIcon className="w-5 h-5 shrink-0" style={{ color: '#fff' } as React.CSSProperties} />
                <a href="mailto:bilvalgas@gmail.com" className="hover:underline break-all">bilvalgas@gmail.com</a>
              </div>
              <div className="flex items-start gap-3">
                <ClockIcon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#fff' } as React.CSSProperties} />
                <div>
                  <p className="font-medium">Tilgjengelig 24/7</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Vi har ingen ordinære åpningstider, men er stort sett tilgjengelig. Ring gjerne for å avtale på forhånd.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl p-6 sm:p-8 shadow-sm space-y-5"
            noValidate>
            
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-2" role="tablist" aria-label="Skjematype">
              {formTabs.map((tab) =>
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors focus:outline-none"
                style={{
                  backgroundColor: activeTab === tab ? 'hsl(218, 65%, 22%)' : 'hsl(218, 30%, 95%)',
                  color: activeTab === tab ? '#fff' : 'hsl(218, 12%, 42%)'
                }}>
                  {tab}
                </button>
              )}
            </div>

            <h3 className="font-display font-semibold text-lg" style={{ color: 'hsl(0, 0%, 12%)' }}>
              {formTitles[activeTab]}
            </h3>

            {/* Name + Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="cf-name" className="block text-sm font-medium mb-1.5" style={{ color: 'hsl(0, 0%, 12%)' }}>
                  Navn
                </label>
                <input
                  id="cf-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'hsl(0, 0%, 98%)',
                    borderColor: 'hsl(218, 20%, 88%)',
                    color: 'hsl(0, 0%, 12%)'
                  }}
                  placeholder="Ditt navn"
                  maxLength={100} />
              </div>
              <div>
                <label htmlFor="cf-phone" className="block text-sm font-medium mb-1.5" style={{ color: 'hsl(0, 0%, 12%)' }}>
                  Telefon
                </label>
                <input
                  id="cf-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'hsl(0, 0%, 98%)',
                    borderColor: 'hsl(218, 20%, 88%)',
                    color: 'hsl(0, 0%, 12%)'
                  }}
                  placeholder="Telefonnummer"
                  maxLength={20} />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="cf-email" className="block text-sm font-medium mb-1.5" style={{ color: 'hsl(0, 0%, 12%)' }}>
                E-post
              </label>
              <input
                id="cf-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'hsl(0, 0%, 98%)',
                  borderColor: 'hsl(218, 20%, 88%)',
                  color: 'hsl(0, 0%, 12%)'
                }}
                placeholder="din@epost.no"
                maxLength={255} />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="cf-message" className="block text-sm font-medium mb-1.5" style={{ color: 'hsl(0, 0%, 12%)' }}>
                Melding
              </label>
              <textarea
                id="cf-message"
                name="message"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 resize-none"
                style={{
                  backgroundColor: 'hsl(0, 0%, 98%)',
                  borderColor: 'hsl(218, 20%, 88%)',
                  color: 'hsl(0, 0%, 12%)'
                }}
                placeholder="Skriv din melding her..."
                maxLength={2000} />
            </div>

            {/* Error message */}
            {submitError && (
              <p className="text-sm rounded-lg px-4 py-2.5" style={{ backgroundColor: 'hsl(0, 80%, 97%)', color: 'hsl(0, 65%, 45%)', border: '1px solid hsl(0, 80%, 88%)' }}>
                {submitError}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-lg font-semibold text-sm hover:brightness-110 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'hsl(218, 55%, 32%)', color: '#fff' }}>
              {submitted ? 'Melding sendt! ✓' : submitting ? 'Sender...' : 'Send melding'}
            </button>
          </form>
        </div>
      </div>
    </section>);

}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-10 sm:py-12" style={{ backgroundColor: 'hsl(218, 65%, 22%)', color: 'rgba(255,255,255,0.7)' }}>
      <div className="container px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
        <a href="#" className="flex items-center gap-3" aria-label="Bilvalg hjem">
          <BilvalLogo white className="opacity-90" />
        </a>
        <p className="text-center sm:text-right">© 2026 Bilvalg AS. Alle rettigheter reservert. Utviklet av Kaewsri Marketing</p>
      </div>
    </footer>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <main>
      <Navbar />
      <HeroSection />
      <CarsSection />
      <ServicesSection />
      <ContactSection />
      <Footer />
    </main>);

}